import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import type { TastingExperience } from '@wineryxplorer/shared';
import { VenueExperienceRow } from './venueExperiences/VenueExperienceRow';
import { Button } from './ui/Button';
import { FormSection } from './ui/FormSection';

/** Which status list the venue-experiences manager is showing. */
type ExperienceTab = 'published' | 'draft' | 'archived';

const TABS: { id: ExperienceTab; label: string }[] = [
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Draft' },
  { id: 'archived', label: 'Archived' },
];

const EMPTY_STATE_COPY: Record<ExperienceTab, string> = {
  published:
    'Nothing here is visible to the public yet. Create an experience, then set its status to Published on its edit page.',
  draft: 'Experiences you are still working on will appear here.',
  archived: 'Experiences you have retired from the list will appear here.',
};

interface VenueTastingExperiencesManagementProps {
  venueId: string;
}

export const VenueTastingExperiencesManagement = ({
  venueId,
}: VenueTastingExperiencesManagementProps) => {
  const navigate = useNavigate();
  const [deletingExperienceId, setDeletingExperienceId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ExperienceTab>('published');
  const utils = trpc.useContext();

  // Fetch experiences filtered by status on the server side for better performance
  const statusFilter =
    activeTab === 'published'
      ? ('published' as const)
      : activeTab === 'draft'
        ? ('draft' as const)
        : ('archived' as const);

  const { data, isPending, error } = trpc.tastingExperience.listByVenue.useQuery({
    venueId,
    status: statusFilter,
  });

  const deleteExperience = trpc.tastingExperience.delete.useMutation({
    onSuccess: () => {
      // Invalidate and refetch experiences for current tab
      utils.tastingExperience.listByVenue.invalidate({ venueId, status: statusFilter });
      setDeletingExperienceId(null);
    },
    onError: (error) => {
      alert(`Error deleting experience: ${error.message}`);
      setDeletingExperienceId(null);
    },
  });

  const handleCreateExperience = () => {
    navigate(`/tasting-experiences/new?venueId=${venueId}`);
  };

  const handleEditExperience = (experienceId: string) => {
    // `from=manage` tells the edit page this flow started at the hub, so it
    // returns here on save or cancel instead of the experience's own page.
    navigate(`/tasting-experiences/${experienceId}/edit?from=manage`);
  };

  const handleDeleteExperience = (experienceId: string) => {
    if (
      confirm(
        'Are you sure you want to delete this tasting experience? This action cannot be undone.'
      )
    ) {
      setDeletingExperienceId(experienceId);
      deleteExperience.mutate({ id: experienceId });
    }
  };

  if (isPending) {
    return (
      <div className="py-8">
        <p className="text-gray-600">Loading tasting experiences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <p className="text-error">Error loading tasting experiences: {error.message}</p>
      </div>
    );
  }

  // Cast experiences to proper type - experiences are already filtered by status on the server
  const sortedExperiences = (data?.items ?? []) as TastingExperience[];

  // Get total count for display
  const totalCount = data?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <FormSection
        title="Your Tasting Experiences"
        titleAs="h3"
        // Leads its card on the venue management hub, so it draws no rule.
        divider={false}
        className="mt-0"
        description={`${totalCount} ${activeTab} ${totalCount === 1 ? 'experience' : 'experiences'}`}
        actions={<Button onClick={handleCreateExperience}>Create Experience</Button>}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Experience status tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? 'border-primary-700 text-primary-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Experiences List */}
      {sortedExperiences.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          {sortedExperiences.map((experience) => (
            <VenueExperienceRow
              key={experience.id}
              experience={experience}
              isDeleting={deletingExperienceId === experience.id}
              onEdit={handleEditExperience}
              onDelete={handleDeleteExperience}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-100 px-6 py-12 text-center">
          <h3 className="font-serif text-lg text-gray-900">No {activeTab} experiences</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
            {EMPTY_STATE_COPY[activeTab]}
          </p>
          {activeTab === 'published' && (
            <div className="mt-6">
              <Button onClick={handleCreateExperience}>Create Your First Experience</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
